import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Megaphone, Trash2, Plus, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";

const fmtDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

const EMPTY_FORM = {
  id: null,
  title: "",
  message: "",
  cta_text: "",
  cta_url: "",
  is_active: true,
};

export const AdminAnnouncements = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/announcements");
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load announcements.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (a) => {
    setForm({
      id: a.id,
      title: a.title || "",
      message: a.message || "",
      cta_text: a.cta_text || "",
      cta_url: a.cta_url || "",
      is_active: !!a.is_active,
    });
    // Scroll the form into view on small screens — admin clicks "Edit" from
    // a long list and expects the editor to appear.
    try {
      document
        .querySelector('[data-testid="admin-announcement-form"]')
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch {
      /* ignore */
    }
  };

  const reset = () => setForm(EMPTY_FORM);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) {
      toast.error("Title and message are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        message: form.message.trim(),
        cta_text: form.cta_text.trim() || null,
        cta_url: form.cta_url.trim() || null,
        is_active: !!form.is_active,
      };
      if (form.id) {
        await api.patch(`/admin/announcements/${form.id}`, payload);
        toast.success("Announcement updated.");
      } else {
        await api.post("/admin/announcements", payload);
        toast.success("Announcement created.");
      }
      reset();
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Couldn't save announcement. Check the fields and try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/admin/announcements/${a.id}`, {
        is_active: !a.is_active,
      });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update status.");
    }
  };

  const remove = async (a) => {
    // Use the native confirm — keeps Phase-1 scope minimal, no extra modal.
    if (!window.confirm(`Delete "${a.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/announcements/${a.id}`);
      toast.success("Announcement deleted.");
      if (form.id === a.id) reset();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete.");
    }
  };

  return (
    <section
      className="mt-10 rounded-2xl bg-white border border-gray-200 overflow-hidden"
      data-testid="admin-announcements-section"
    >
      <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
            <Megaphone className="w-4 h-4 text-emerald-700" />
          </span>
          <div>
            <h2 className="font-display text-lg font-semibold text-gray-900">
              Announcements
            </h2>
            <p className="text-xs text-gray-500">
              Banners shown to all signed-in users on their dashboard.
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="admin-announcements-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </header>

      <form
        onSubmit={submit}
        className="px-5 py-5 border-b border-gray-100 grid gap-4 sm:grid-cols-2"
        data-testid="admin-announcement-form"
      >
        <div className="sm:col-span-2 flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-gray-500">
          <Plus className="w-3.5 h-3.5" />
          {form.id ? "Edit announcement" : "New announcement"}
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="ann-title">Title *</Label>
          <Input
            id="ann-title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="New: Rebooking Reminders"
            maxLength={200}
            required
            className="mt-1.5 h-10"
            data-testid="admin-announcement-title-input"
          />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="ann-message">Message *</Label>
          <Textarea
            id="ann-message"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder="Bring past customers back with one-click follow-up emails."
            rows={3}
            maxLength={2000}
            required
            className="mt-1.5"
            data-testid="admin-announcement-message-input"
          />
        </div>
        <div>
          <Label htmlFor="ann-cta-text">CTA text (optional)</Label>
          <Input
            id="ann-cta-text"
            value={form.cta_text}
            onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
            placeholder="Try it now"
            maxLength={80}
            className="mt-1.5 h-10"
            data-testid="admin-announcement-cta-text-input"
          />
        </div>
        <div>
          <Label htmlFor="ann-cta-url">CTA URL (optional)</Label>
          <Input
            id="ann-cta-url"
            value={form.cta_url}
            onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
            placeholder="/dashboard or https://example.com"
            maxLength={2000}
            className="mt-1.5 h-10"
            data-testid="admin-announcement-cta-url-input"
          />
          <p className="mt-1 text-[11px] text-gray-500">
            Use a path like <span className="font-mono">/dashboard</span> for in-app links, or a full https:// URL for external.
          </p>
        </div>
        <div className="sm:col-span-2 flex items-center gap-3 pt-1">
          <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) =>
                setForm({ ...form, is_active: e.target.checked })
              }
              className="w-4 h-4 accent-emerald-700"
              data-testid="admin-announcement-active-input"
            />
            Active (visible to users)
          </label>
          <div className="ml-auto flex items-center gap-2">
            {form.id ? (
              <Button
                type="button"
                variant="outline"
                onClick={reset}
                data-testid="admin-announcement-cancel"
              >
                Cancel
              </Button>
            ) : null}
            <Button
              type="submit"
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="admin-announcement-save"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : form.id ? (
                "Save changes"
              ) : (
                "Create announcement"
              )}
            </Button>
          </div>
        </div>
      </form>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="admin-announcements-table">
          <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3 font-semibold">Title</th>
              <th className="text-left px-5 py-3 font-semibold">Status</th>
              <th className="text-left px-5 py-3 font-semibold">Created</th>
              <th className="text-right px-5 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-5 py-10 text-center text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin inline-block" />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-5 py-10 text-center text-red-600"
                  data-testid="admin-announcements-error"
                >
                  {error}
                </td>
              </tr>
            ) : list.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-5 py-10 text-center text-gray-500"
                  data-testid="admin-announcements-empty"
                >
                  No announcements yet. Create one above to show a banner on every user&apos;s dashboard.
                </td>
              </tr>
            ) : (
              list.map((a) => (
                <tr
                  key={a.id}
                  className="hover:bg-gray-50 transition-colors"
                  data-testid={`admin-announcement-row-${a.id}`}
                >
                  <td className="px-5 py-3">
                    <div className="font-medium text-gray-900">{a.title}</div>
                    <div className="text-xs text-gray-500 line-clamp-1 max-w-md">
                      {a.message}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <button
                      type="button"
                      onClick={() => toggleActive(a)}
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-colors ${
                        a.is_active
                          ? "bg-emerald-50 text-emerald-800 border-emerald-100 hover:bg-emerald-100"
                          : "bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200"
                      }`}
                      data-testid={`admin-announcement-toggle-${a.id}`}
                    >
                      {a.is_active ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="px-5 py-3 text-gray-600">{fmtDate(a.created_at)}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => startEdit(a)}
                        data-testid={`admin-announcement-edit-${a.id}`}
                      >
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => remove(a)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`admin-announcement-delete-${a.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};
